import { FC } from 'react';
import clsx from 'clsx';

import { RadioButtonProps } from './RadioButton.types';
import { StyledRadioButtonDiv } from './RadioButton.styles';

// the attributes which are required to pass as props for radio button as follows:
// radiobuttonId, radiobuttonName, radiobuttonLabel, checked, onChange, value,
// custom_click_track_value, disabled, setTabIndex, customClickClass

export const RadioButton: FC<RadioButtonProps> = ({
  radiobuttonId,
  radiobuttonName,
  radiobuttonLabel,
  checked,
  onChange,
  className,
  value,
  disabled,
  setTabIndex,
  customClickClass,
  isRichText,
  labelClassName,
  onKeyDown,
  dataTestId,
  tabIndexForInput,
  ariaLabelForLink,
  isRoleAndAriaEnabled = false,
  ...props
}) => {
  return (
    <StyledRadioButtonDiv
      data-component-name="a-ui-library-RadioButton"
      data-testid="ui-library-RadioButton"
      className={className ?? ''}
    >
      <input
        type="radio"
        id={radiobuttonId}
        name={radiobuttonName}
        onChange={onChange}
        checked={checked}
        value={value}
        disabled={disabled}
        className={customClickClass}
        aria-checked={checked}
        /**  this condition checks if developer wants focus on input or label , 
         if focus is not required on input, setting aria-hidden=true, so that we do not consider single 
         radio button as 2 separate radio buttons
         */
        {...(tabIndexForInput &&
          tabIndexForInput < 0 && {
            'aria-hidden': true,
          })}
        /**  this condition checks if developer wants focus on label or not , 
         if focus is not required on label, setting aria-label for input separately , so that radio button has a
         label even if label has tabindex=-1
         */
        {...((!tabIndexForInput || (tabIndexForInput && tabIndexForInput >= 0)) && {
          'aria-label': `${isRoleAndAriaEnabled ? `${ariaLabelForLink} link` : radiobuttonLabel}`,
        })}
        tabIndex={tabIndexForInput}
        onKeyDown={event => {
          onKeyDown?.(event, value, radiobuttonLabel);
        }}
        {...props}
      />
      {isRichText ? (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className={clsx(labelClassName ?? '')} htmlFor={radiobuttonId}>
          <span
            tabIndex={setTabIndex}
            className={clsx('keydown-anchor', disabled ? 'disabled' : '')}
            onKeyDown={event => {
              onKeyDown?.(event, value, radiobuttonLabel);
            }}
            data-testid={dataTestId}
            dangerouslySetInnerHTML={{ __html: radiobuttonLabel ?? '' }}
            {...(!isRoleAndAriaEnabled && {
              'aria-label': `${ariaLabelForLink || radiobuttonLabel} link`,
              role: 'radio',
              'aria-checked': checked,
            })}
            {...(disabled && {
              'aria-disabled': true,
            })}
          ></span>
        </label>
      ) : (
        <label className={clsx(labelClassName ?? '')} htmlFor={radiobuttonId}>
          <span
            tabIndex={setTabIndex}
            className={clsx('keydown-anchor', disabled ? 'disabled' : '')}
            onKeyDown={event => {
              onKeyDown?.(event, value, radiobuttonLabel);
            }}
            data-testid={dataTestId}
            {...(!isRoleAndAriaEnabled && {
              'aria-label': `${ariaLabelForLink || radiobuttonLabel} link`,
              role: 'radio',
              'aria-checked': checked,
            })}
            {...(setTabIndex &&
              setTabIndex < 0 && {
                'aria-hidden': true,
              })}
            {...(disabled && {
              'aria-disabled': true,
            })}
          >
            {' '}
            {radiobuttonLabel}{' '}
          </span>
        </label>
      )}
    </StyledRadioButtonDiv>
  );
};
